import React, { useEffect, useState, useMemo } from "react";
import { api, money, formatApiError } from "../../lib/api";
import { Clock, Mail, Phone, Copy, AlertOctagon, DollarSign } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin dashboard widget — Abandoned checkout recovery.
 *
 * Source of truth: the same `orders` collection used for paid orders. An
 * "abandoned" cart is simply a pending order older than the configurable
 * threshold (default 60 min, env: ABANDONED_CART_THRESHOLD_MINUTES). We do
 * NOT maintain a parallel checkout_sessions table — that would risk
 * duplicates and drift with the real order record.
 *
 * Privacy: only contact + cart data is shown. No payment-instrument data
 * is ever stored or surfaced (Stripe owns it).
 */

const RECOVERY_TEMPLATE = (name) =>
  `Hello${name ? ` ${name}` : ""}, this is Lecis Auto Glass. We noticed you started an order but may not have completed checkout. If you need help confirming fitment or completing your order, we’d be happy to assist.`;

function timeAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function AbandonedCartsWidget() {
  const [stats, setStats] = useState(null);
  const [carts, setCarts] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/admin/abandoned-carts/stats"),
        api.get("/admin/abandoned-carts?limit=10"),
      ]);
      setStats(s.data);
      setCarts(c.data.carts || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const copyRecovery = (cart) => {
    if (!cart.customer_email) {
      toast.error("No customer email on this cart");
      return;
    }
    const msg = RECOVERY_TEMPLATE(cart.customer_name);
    const payload = `To: ${cart.customer_email}\nSubject: Need help completing your order?\n\n${msg}`;
    navigator.clipboard.writeText(payload).then(
      () => toast.success("Recovery email copied to clipboard"),
      () => toast.error("Clipboard not available — copy manually")
    );
  };

  const threshold = stats?.threshold_minutes || 60;

  return (
    <div className="bg-white border border-zinc-200 rounded-md mb-10" data-testid="abandoned-carts-widget">
      <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
        <div>
          <h2 className="font-display tracking-tight flex items-center gap-2">
            <AlertOctagon size={16} className="text-amber-500" /> Abandoned checkouts
          </h2>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Pending checkouts older than {threshold} min. Stripe webhook is source of truth for paid status.
          </p>
        </div>
        <button onClick={load} className="text-xs text-zinc-500 hover:text-zinc-900" data-testid="abandoned-refresh">Refresh</button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4 p-5 border-b border-zinc-100">
        <Kpi
          label="Today"
          value={stats?.abandoned_today ?? "—"}
          icon={<AlertOctagon size={14} className="text-amber-500" />}
          highlight={(stats?.abandoned_today || 0) > 0}
          testid="kpi-abandoned-today"
        />
        <Kpi
          label="Lost revenue today"
          value={stats ? money(stats.lost_revenue_today || 0) : "—"}
          icon={<DollarSign size={14} className="text-amber-500" />}
          highlight={(stats?.lost_revenue_today || 0) > 0}
          testid="kpi-lost-revenue"
        />
        <Kpi
          label="Total pending"
          value={stats?.total_abandoned ?? "—"}
          icon={<Clock size={14} className="text-zinc-400" />}
          testid="kpi-total-abandoned"
        />
      </div>

      {/* Recent list */}
      <table className="w-full text-sm" data-testid="abandoned-carts-table">
        <thead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-50">
          <tr>
            <th className="text-left px-6 py-3">Cart</th>
            <th className="text-left px-6 py-3">Contact</th>
            <th className="text-left px-6 py-3">Items</th>
            <th className="text-left px-6 py-3">Abandoned</th>
            <th className="text-right px-6 py-3">Cart total</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan={5} className="text-center text-zinc-500 py-10">Loading…</td></tr>
          )}
          {!loading && carts.length === 0 && (
            <tr><td colSpan={5} className="text-center text-zinc-500 py-10">No abandoned carts. 🎉</td></tr>
          )}
          {!loading && carts.map((c) => (
            <tr
              key={c.id}
              data-testid={`abandoned-row-${c.id.slice(0, 8)}`}
              onClick={() => setActive(c)}
              className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer"
            >
              <td className="px-6 py-3 font-mono text-xs">{c.id.slice(0, 8)}</td>
              <td className="px-6 py-3">
                <div className="text-zinc-900 text-sm">{c.customer_email || <span className="text-zinc-400">—</span>}</div>
                {c.customer_phone && <div className="text-xs text-zinc-500">{c.customer_phone}</div>}
              </td>
              <td className="px-6 py-3 text-zinc-600 text-xs">{c.items?.length || 0}</td>
              <td className="px-6 py-3 text-xs text-zinc-600 font-mono">{timeAgo(c.created_at)}</td>
              <td className="px-6 py-3 text-right font-mono">{money(c.subtotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Detail drawer */}
      {active && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setActive(null)} />
          <div className="fixed top-0 right-0 z-50 h-full w-full max-w-lg bg-white border-l border-zinc-200 shadow-xl flex flex-col" data-testid="abandoned-detail">
            <div className="px-6 py-5 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Abandoned cart</div>
                <div className="font-mono text-sm">{active.id}</div>
              </div>
              <button onClick={() => setActive(null)} className="text-zinc-500 hover:text-zinc-900">Close</button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Customer</div>
                <div className="space-y-1 text-sm">
                  <Row icon={<Mail size={12} />} value={active.customer_email || "—"} />
                  <Row icon={<Phone size={12} />} value={active.customer_phone || "—"} />
                  {active.customer_name && <div className="text-zinc-600 text-sm">{active.customer_name}</div>}
                </div>
                {active.customer_email && (
                  <button
                    type="button"
                    data-testid="copy-recovery-btn"
                    onClick={() => copyRecovery(active)}
                    className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-2 bg-zinc-900 text-white rounded-sm hover:bg-zinc-800"
                  >
                    <Copy size={12} /> Copy recovery email
                  </button>
                )}
              </div>

              {active.notes && (
                <div data-testid="abandoned-notes">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Order notes</div>
                  <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded p-3 whitespace-pre-wrap break-words">
                    {active.notes}
                  </div>
                </div>
              )}

              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">
                  Cart contents ({active.items?.length || 0})
                </div>
                <ul className="space-y-2">
                  {(active.items || []).map((it) => (
                    <li key={it.product_id} className="flex items-start justify-between text-sm border-b border-zinc-100 pb-2">
                      <div>
                        <div>{it.name}</div>
                        <div className="text-xs text-zinc-500 font-mono">SKU {it.sku} × {it.quantity}</div>
                      </div>
                      <div className="font-mono">{money(it.price * it.quantity)}</div>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between pt-3 font-mono">
                  <span className="text-zinc-500 text-sm">Cart total</span>
                  <span>{money(active.subtotal)}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <Meta label="Status" value={active.payment_status} />
                <Meta label="Checkout started" value={new Date(active.created_at).toLocaleString()} />
                <Meta label="Last activity" value={active.last_activity_at ? new Date(active.last_activity_at).toLocaleString() : "—"} />
                <Meta label="Time since start" value={timeAgo(active.created_at)} />
              </div>

              {active.stripe_session_id && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-1">Stripe session ID</div>
                  <code className="text-[11px] text-zinc-600 break-all font-mono">{active.stripe_session_id}</code>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, icon, highlight, testid }) {
  return (
    <div className={`border ${highlight ? "border-amber-300 bg-amber-50/40" : "border-zinc-200"} rounded p-4`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">{label}</div>
        {icon}
      </div>
      <div className="text-2xl font-mono font-medium">{value}</div>
    </div>
  );
}

function Row({ icon, value }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-zinc-400">{icon}</span>
      <span>{value}</span>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="border border-zinc-200 rounded p-2.5">
      <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</div>
      <div className="text-zinc-800 break-all">{value}</div>
    </div>
  );
}
